import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { connect, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { Spinner } from "../elements";
import {
  fetchEventDetails,
  clearEventDetails
} from "../../state/events/actions";
import {
  fetchAuthorizedEvent,
  clearAuthorizedEvent,
  storeNextUrl
} from "../../state/user/actions";
import eventStreamPropType from "../../prop-types/eventStream";
import {
  EVENTS,
  PRODUCTS,
  PLAYER,
  EVENT,
  VIDEO,
  AUDIO,
  CREATE_ACCOUNT,
  LOGIN
} from "../../constants";
import { getItemFromLS } from "../../utils/util";

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node).isRequired
  ]).isRequired,
  // mapStateToProps
  event: eventStreamPropType.isRequired,
  authorizedEventAccess: PropTypes.shape({
    authorized: PropTypes.bool,
    accountId: PropTypes.number,
    eventId: PropTypes.number
  }).isRequired,
  loggedIn: PropTypes.bool.isRequired,
  userId: PropTypes.number,
  // mapDispatchToProps
  fetchAuthorizedEventAction: PropTypes.func.isRequired,
  clearAuthorizedEventAction: PropTypes.func.isRequired,
  fetchEventDetailsAction: PropTypes.func.isRequired,
  clearEventDetailsAction: PropTypes.func.isRequired,
  storeNextUrlAction: PropTypes.func.isRequired
};

const defaultProps = {
  userId: null
};

export function PPVMiddleware(props) {
  const {
    children,
    event,
    authorizedEventAccess,
    loggedIn,
    userId,
    fetchAuthorizedEventAction,
    clearAuthorizedEventAction,
    fetchEventDetailsAction,
    clearEventDetailsAction,
    storeNextUrlAction
  } = props;

  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirectType = queryParams.get("redirectTo");

  const { requireRegistration } = useSelector(
    (state) => state.client.client.features
  );

  const [ready, setReady] = useState(false);

  const redirectToPurchasePage = () => {
    const { shortName: clientUsername } = params;
    storeNextUrlAction(
      `/${clientUsername}/${PLAYER}/${EVENT}/${event.id}/${event.streamType}${location.search}`
    );
    history.push(
      `/${clientUsername}/${EVENTS}/${event.id}/${PRODUCTS}${location.search}`
    );
  };

  const redirectToRegSignInPage = (type) => {
    const { shortName } = params;
    const pathName = `/${shortName}/${PLAYER}/${EVENT}/${event.id}/${event.streamType}`;
    queryParams.delete("redirectTo");
    const keys = Array.from(queryParams.keys());
    const queryStr = keys.length ? `?${queryParams.toString()}` : "";
    storeNextUrlAction(`${pathName}${queryStr}`);
    const redirectTo =
      type === "create"
        ? `/${shortName}/${CREATE_ACCOUNT}${queryStr}`
        : `/${shortName}/${LOGIN}${queryStr}`;
    history.push(redirectTo);
  };

  useEffect(() => {
    const { eventId: id, streamType } = params;
    fetchEventDetailsAction({ id, streamType: streamType || VIDEO });
    return () => {
      clearEventDetailsAction();
      clearAuthorizedEventAction();
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(event)) {
      const { id: eventId, ppv, streamType } = event;
      if (!ppv || streamType === AUDIO) {
        // Event is FREE or is AUDIO
        if (!loggedIn && requireRegistration && redirectType) {
          // User is not logged in and the client requires registration
          if (redirectType === "create") {
            redirectToRegSignInPage("create");
          } else if (redirectType === "signin") {
            redirectToRegSignInPage("signin");
          }
        } else {
          setReady(true);
        }
      } else if (loggedIn) {
        // Event is PPV and is VIDEO
        // Check if user is authorized to view event
        if (isEmpty(authorizedEventAccess)) {
          fetchAuthorizedEventAction(userId, eventId);
        } else {
          const { authorized } = authorizedEventAccess;
          setReady(authorized);
          if (!authorized) {
            redirectToPurchasePage();
          }
        }
      } else if (!getItemFromLS("tokenId")) {
        redirectToPurchasePage();
      }
    }
  }, [event, authorizedEventAccess, loggedIn, userId]);

  return ready ? (
    <>{children}</>
  ) : (
    <div className="d-flex justify-content-center align-items-center h-100 w-100">
      <Spinner />
    </div>
  );
}

PPVMiddleware.propTypes = propTypes;
PPVMiddleware.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  userId: state.user.user.id,
  event: state.events.events.selected.data,
  authorizedEventAccess: state.user.authorizedEventAccess,
  loggedIn: state.user.loggedIn
});

const mapDispatchToProps = {
  fetchAuthorizedEventAction: fetchAuthorizedEvent,
  clearAuthorizedEventAction: clearAuthorizedEvent,
  fetchEventDetailsAction: fetchEventDetails,
  clearEventDetailsAction: clearEventDetails,
  storeNextUrlAction: storeNextUrl
};

export default connect(mapStateToProps, mapDispatchToProps)(PPVMiddleware);
